import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import ResourcePreviewModal from "@/components/ResourcePreviewModal";
import { useTanyaTrigger } from "@/hooks/useTanyaTrigger";
import { assetFileUrl } from "@/lib/assetUrl";
import { useVisitorContext } from "@/contexts/VisitorTrackingContext";


/**
 * Global resource preview.
 *
 * Any card on the site (RecommendedResources, RelatedContent, Tanya CTAs, etc.)
 * can call `openResourcePreview(id)` to open the same in-place preview modal
 * the Resources index uses. Open resources show the file; gated resources are
 * handed off to Tanya for lead capture, matching the Resources page flow.
 */

interface ResourceRow {
  id: string;
  title: string;
  slug: string | null;
  file_url: string | null;
  file_path: string | null;
  resource_type: string;
  access_type: string;
  is_document: boolean;
}


interface PreviewState {
  open: boolean;
  resource: ResourceRow | null;
}

interface Ctx {
  openResourcePreview: (resourceId: string) => Promise<void>;
}

const ResourcePreviewCtx = createContext<Ctx | null>(null);

export function ResourcePreviewProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PreviewState>({ open: false, resource: null });
  const cache = useRef<Map<string, ResourceRow>>(new Map());
  const { openTanya } = useTanyaTrigger();
  const { trackEvent } = useVisitorContext();
  // One resource_viewed per resource per session, the way sentPages works.
  const viewedResources = useRef<Set<string>>(new Set());

  const fetchResource = useCallback(async (id: string): Promise<ResourceRow | null> => {
    const cached = cache.current.get(id);
    if (cached) return cached;
    const { data, error } = await supabase
      .from("asset")
      .select("id, title, slug, file_url, file_path, resource_type, access_type, current_version:asset_version!asset_current_version_id_fkey(source_kind)")
      .eq("id", id)
      .eq("published", true)
      .eq("visibility", "public")
      .maybeSingle();
    if (error || !data) return null;
    const row: ResourceRow = {
      id: data.id,
      title: data.title,
      slug: (data as any).slug || null,
      file_url: (data as any).file_url || null,
      file_path: (data as any).file_path || null,
      resource_type: (data as any).resource_type || "pdf",
      access_type: (data as any).access_type || "open",
      is_document: (data as any).current_version?.source_kind === "html",
    };
    cache.current.set(id, row);
    return row;
  }, []);

  const openResourcePreview = useCallback(async (resourceId: string) => {
    const row = await fetchResource(resourceId);
    if (!row) return;
    if (!viewedResources.current.has(row.id)) {
      viewedResources.current.add(row.id);
      trackEvent("resource_viewed", {
        resource_id: row.id,
        title: row.title,
        slug: row.slug,
        is_document: row.is_document,
      });
    }
    if (row.access_type !== "open") {
      // Gated: hand off to Tanya immediately, same as the Resources page.
      openTanya("download-resources", row.title, { assetId: row.id });
      return;
    }
    setState({ open: true, resource: row });
  }, [fetchResource, openTanya, trackEvent]);


  const handleOpenChange = useCallback((open: boolean) => {
    setState((prev) => ({ open, resource: open ? prev.resource : null }));
  }, []);

  const handleDownload = useCallback(() => {
    const r = state.resource;
    if (!r) return;
    // Match Resources page: close the preview and route the download through Tanya.
    setState({ open: false, resource: null });
    openTanya("download-resources", r.title, { assetId: r.id });
  }, [state.resource, openTanya]);

  const value = useMemo(() => ({ openResourcePreview }), [openResourcePreview]);

  return (
    <ResourcePreviewCtx.Provider value={value}>
      {children}
      {state.resource && (
        <ResourcePreviewModal
          open={state.open}
          onOpenChange={handleOpenChange}
          title={state.resource.title}
          fileUrl={assetFileUrl(
            state.resource.id,
            state.resource.file_url,
            state.resource.file_path,
          ) || ""}
          resourceType={state.resource.resource_type}
          accessType={state.resource.access_type}
          onDownload={handleDownload}
        />
      )}
    </ResourcePreviewCtx.Provider>
  );
}

export function useResourcePreview(): Ctx {
  const ctx = useContext(ResourcePreviewCtx);
  if (!ctx) {
    // Safe no-op fallback so components don't crash if rendered outside provider
    // (e.g. isolated Storybook/test). In the app it's mounted at the root.
    return {
      openResourcePreview: async () => {
        if (typeof window !== "undefined") {
          console.warn("[ResourcePreview] Provider not mounted; cannot open preview.");
        }
      },
    };
  }
  return ctx;
}